import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart,
  ChevronRight,
  Infinity as InfinityIcon,
  Info,
  ArrowLeftRight,
  MessageSquare,
  Hotel,
  Utensils,
  Plane,
  Camera,
  Bus,
  UserRound,
  MapPin,
  X,
  CheckCircle2,
  StickyNote as StampIcon,
  Zap,
} from "lucide-react";
import { SmartImage } from "@/components/SmartImage";
import { formatINR } from "@/data/tours";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EnquiryDrawer } from "@/components/india/EnquiryForm";
import { useCompare } from "@/context/CompareContext";

const Badge = ({ children, tone }) => {
  const tones = {
    orange: "text-[var(--yellow-2)] border-[#f59e0b]/40 bg-[#fcd34d]/10",
    red: "text-[var(--red)] border-[#e11d2e]/30 bg-[#e11d2e]/5",
    code: "bg-[var(--navy)] text-white border-transparent",
  };
  return (
    <span className={`inline-flex items-center rounded-[3px] border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
};

// ── Tour Highlights popup
const HighlightsDialog = ({ open, onOpenChange, tour }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="highlights-dialog">
      <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
        <DialogTitle className="text-[var(--navy)] text-base underline decoration-[var(--yellow-2)] decoration-2 underline-offset-4">Tour Highlights</DialogTitle>
        <DialogDescription className="sr-only">Key highlights included on this tour.</DialogDescription>
      </DialogHeader>
      <ul className="px-5 py-4 space-y-2.5 max-h-[320px] overflow-y-auto thin-scroll">
        {tour.highlights.map((h) => (
          <li key={h} className="flex items-center gap-2.5 text-[13.5px] text-[#0b2545]/85">
            <MapPin className="w-3.5 h-3.5 text-[var(--yellow-2)] shrink-0" />
            {h}
          </li>
        ))}
      </ul>
    </DialogContent>
  </Dialog>
);

// ── Tour Includes popup
const inclusionItems = (inc) => [
  { key: "hotel", label: "Hotel", Icon: Hotel, on: inc.hotel },
  { key: "meals", label: "Meals", Icon: Utensils, on: inc.meals },
  { key: "flight", label: "Flight", Icon: Plane, on: inc.flight, optional: inc.flight === "optional" },
  { key: "sightseeing", label: "Sightseeing", Icon: Camera, on: inc.sightseeing },
  { key: "transport", label: "Transport", Icon: Bus, on: inc.transport },
  ...(inc.visa !== undefined ? [{ key: "visa", label: "Visa", Icon: StampIcon, on: inc.visa }] : []),
];

const InclusionsDialog = ({ open, onOpenChange, tour }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="inclusions-dialog">
      <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
        <DialogTitle className="text-[var(--navy)] text-base">Tour Includes</DialogTitle>
        <DialogDescription className="sr-only">Services and facilities included in this package.</DialogDescription>
      </DialogHeader>
      <div className="px-6 py-5">
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-4 text-center">
          {inclusionItems(tour.inclusions).map(({ key, label, Icon, on, optional }) => (
            <div key={key} className={`flex flex-col items-center gap-1.5 w-16 ${on ? "" : "opacity-30"}`}>
              <div className="relative">
                {optional && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[var(--yellow)] text-[var(--navy)] text-[7px] font-bold px-1 rounded">Optional</span>
                )}
                <Icon className="w-6 h-6 text-[var(--yellow-2)]" strokeWidth={1.6} />
              </div>
              <span className="text-[11px] text-[#0b2545]/80">{label}</span>
            </div>
          ))}
        </div>
        <div className="mt-5 flex items-center gap-3 text-[13px] text-[#0b2545]/80 border-t border-gray-100 pt-4">
          <UserRound className="w-6 h-6 text-[var(--navy)] shrink-0" strokeWidth={1.4} />
          Tour includes the services of Royal Tours' Tour Manager.
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// ── Departure Dates popup
export const DatesDialog = ({ open, onOpenChange, tour, onSelect }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="dates-dialog">
      <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100">
        <DialogTitle className="text-[var(--navy)] text-lg">{tour.title}</DialogTitle>
        <DialogDescription className="sr-only">Available departure dates and prices for this tour.</DialogDescription>
      </DialogHeader>
      <div className="grid md:grid-cols-2 gap-6 px-6 py-5 max-h-[60vh] overflow-y-auto thin-scroll">
        <div>
          <div className="flex items-center gap-4 text-[13px] text-[#0b2545]/80 mb-4">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[var(--yellow-2)]" /> You will visit</span>
          </div>
          <div className="flex items-center gap-4 text-[13px] font-semibold text-[var(--navy)] mb-4">
            <span>{tour.statesCount} States</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span>{tour.citiesCount} Cities</span>
          </div>
          <div className="space-y-3">
            {tour.route.map((r) => (
              <div key={r.state}>
                <div className="text-[13px] font-bold text-[var(--navy)] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--navy)]" /> {r.state}
                </div>
                <ul className="pl-4 mt-1 space-y-1">
                  {r.stops.map((s) => (
                    <li key={s} className="text-[13px] text-[#0b2545]/70">{s}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[13px] font-semibold text-[var(--navy)] mb-3 inline-flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-[var(--yellow-2)]" /> Departure dates ({tour.dates.length})
          </div>
          <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
            {tour.dates.length ? tour.dates.map((d) => (
              <div key={d.date} className="flex items-center justify-between px-3.5 py-3">
                <div>
                  <div className="text-[13.5px] font-medium text-[var(--navy)]">{d.date}</div>
                  {d.note && <div className="text-[11px] text-[var(--red-2)] font-medium">{d.note}</div>}
                </div>
                <div className="text-right">
                  <div className="text-[14px] font-bold text-[var(--navy)]">{d.price != null ? formatINR(d.price) : "Price on request"}</div>
                  {d.tag && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded mt-0.5">
                      ↓ {d.tag}
                    </span>
                  )}
                </div>
              </div>
            )) : (
              <p className="px-3.5 py-4 text-[13px] text-gray-600">Upcoming departures are available on request. Enquire with our travel team for current dates and pricing.</p>
            )}
          </div>
        </div>
      </div>
      <div className="px-6 py-4 border-t border-gray-100 bg-[var(--soft)]">
        <Button
          data-testid="select-dates-btn"
          onClick={() => { onOpenChange(false); onSelect?.(); }}
          className="w-full bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-semibold rounded-md"
        >
          {tour.dates.length ? "Select Dates" : "Enquire about dates"}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const TourCard = ({ tour, view = "list", index = 0 }) => {
  const navigate = useNavigate();
  const { isIn, toggle: toggleCompare } = useCompare();
  const compared = isIn(tour.slug);
  const [liked, setLiked] = useState(false);
  const [popup, setPopup] = useState(null); // highlights | inclusions | dates
  const [enquiry, setEnquiry] = useState(false);

  const goDetail = () => navigate(`/tour/${tour.slug}`);
  const highlightsText = tour.highlights.slice(0, 4).join(", ");

  const Popups = (
    <>
      <HighlightsDialog open={popup === "highlights"} onOpenChange={(v) => !v && setPopup(null)} tour={tour} />
      <InclusionsDialog open={popup === "inclusions"} onOpenChange={(v) => !v && setPopup(null)} tour={tour} />
      <DatesDialog open={popup === "dates"} onOpenChange={(v) => !v && setPopup(null)} tour={tour} onSelect={goDetail} />
      <EnquiryDrawer open={enquiry} onOpenChange={setEnquiry} tourTitle={`${tour.code}-${tour.title}`} />
    </>
  );

  const Badges = (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center gap-1 rounded-[3px] overflow-hidden text-[9px] font-bold uppercase tracking-wider">
        <span className="bg-[#fcd34d]/15 text-[var(--yellow-2)] border border-[#f59e0b]/40 border-r-0 px-1.5 py-0.5">{tour.tourType}</span>
        <span className="bg-[var(--navy)] text-white px-1.5 py-0.5">{tour.code}</span>
      </span>
      {tour.speciality && <Badge tone="red">{tour.speciality}</Badge>}
    </div>
  );

  const HeartBtn = (
    <button
      type="button"
      data-testid={`wishlist-${tour.slug}`}
      onClick={() => setLiked((v) => !v)}
      className="absolute top-2.5 left-2.5 w-8 h-8 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white hover:bg-black/60 transition"
      aria-label="wishlist"
    >
      <Heart className={`w-4 h-4 ${liked ? "fill-[var(--red)] text-[var(--red)]" : ""}`} />
    </button>
  );

  const MetaRow = (
    <div className="flex flex-wrap items-center gap-2 text-[13px] text-[var(--navy)] font-medium">
      <span><b>{tour.days}</b> Days</span>
      <span className="w-1 h-1 rounded-full bg-gray-300" />
      {tour.countriesCount ? (
        <>
          <span><b>{tour.countriesCount}</b> {tour.countriesCount > 1 ? "Countries" : "Country"}</span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
        </>
      ) : null}
      <span><b>{tour.citiesCount}</b> Cities</span>
      <span className="w-1 h-1 rounded-full bg-gray-300" />
      <button
        type="button"
        onClick={() => setPopup("dates")}
        data-testid={`dates-trigger-${tour.slug}`}
        className="inline-flex items-center gap-0.5 font-semibold text-[var(--navy)] underline decoration-dotted hover:text-[var(--yellow-2)]"
      >
        {tour.dates.length === 0 ? "Ask for dates" : `${tour.dates.length} ${tour.dates.length === 1 ? "Date" : "Dates"}`} <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  const fillingDate = tour.dates.find((d) => d.tag === "Dates Filling Fast" || d.seats);
  const FillingRow = fillingDate ? (
    <div className="text-[12px] font-semibold text-[var(--red)] flex items-center gap-1.5">
      <Zap className="w-3.5 h-3.5" /> Dates Filling Fast
      <span className="text-[#0b2545]/70 font-normal">{fillingDate.date}{fillingDate.seats ? ` (${fillingDate.seats} seats)` : ""}</span>
    </div>
  ) : null;

  const PopularBadge = tour.popularToday ? (
    <span className="absolute top-2.5 right-2.5 bg-[var(--yellow)] text-[var(--navy)] text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded shadow">Popular Today</span>
  ) : null;

  const AllInclusive = tour.allInclusive && (
    <button
      type="button"
      onClick={() => setPopup("inclusions")}
      data-testid={`inclusions-trigger-${tour.slug}`}
      className="inline-flex items-center gap-1 text-[13px] font-medium text-[var(--navy)] hover:text-[var(--yellow-2)]"
    >
      <InfinityIcon className="w-4 h-4" /> <span className="underline underline-offset-2">All Inclusive</span> <Info className="w-3.5 h-3.5 text-gray-400" />
    </button>
  );

  const Highlights = (
    <div>
      <div className="text-[12px] font-semibold text-green-700 mb-0.5">Tour Highlights</div>
      <p className="text-[13px] text-[#0b2545]/75 leading-snug line-clamp-2">
        {highlightsText}
        {tour.highlights.length > 4 ? "…" : ""}
      </p>
      {tour.highlights.length > 4 && (
        <button type="button" onClick={() => setPopup("highlights")} data-testid={`highlights-trigger-${tour.slug}`} className="mt-0.5 text-[12.5px] font-semibold text-[var(--navy)] underline hover:text-[var(--yellow-2)]">
          More
        </button>
      )}
    </div>
  );

  const PriceBox = (
    <div className="bg-[var(--soft)] border border-gray-100 rounded-lg p-3">
      <div className="text-[11px] text-gray-500">Starting price per person</div>
      <div className="text-[19px] font-bold text-[var(--navy)] leading-tight">{formatINR(tour.startingPrice)}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">
        EMI from <span className="text-[var(--navy)] font-semibold underline">{tour.emiPerMonth == null ? "Price on request" : formatINR(tour.emiPerMonth)}</span>{tour.emiPerMonth == null ? "" : " /month"}
      </div>
    </div>
  );

  const ViewBtn = (
    <Button
      onClick={goDetail}
      data-testid={`view-details-${tour.slug}`}
      className="bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-semibold rounded-md"
    >
      View Details
    </Button>
  );

  const Footer = (
    <div className="flex items-center justify-around border-t border-gray-100 pt-2.5 text-[13px]">
      <button
        type="button"
        onClick={() => toggleCompare(tour)}
        data-testid={`compare-${tour.slug}`}
        className={`inline-flex items-center gap-1.5 font-medium transition ${compared ? "text-[var(--yellow-2)]" : "text-[#0b2545]/80 hover:text-[var(--yellow-2)]"}`}
      >
        <ArrowLeftRight className="w-4 h-4" /> {compared ? "Added" : "Compare"}
      </button>
      <button
        type="button"
        onClick={() => setEnquiry(true)}
        data-testid={`enquire-${tour.slug}`}
        className="inline-flex items-center gap-1.5 font-medium text-[#0b2545]/80 hover:text-[var(--yellow-2)]"
      >
        <MessageSquare className="w-4 h-4" /> Enquire Now
      </button>
    </div>
  );

  if (view === "grid") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5, delay: (index % 3) * 0.06 }}
        className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-[0_20px_40px_-20px_rgba(11,37,69,0.35)] hover:border-[#f59e0b]/40 transition-all flex flex-col"
        data-testid={`tour-card-${tour.slug}`}
      >
        <div className="relative h-44 overflow-hidden">
          <SmartImage src={tour.image} alt={tour.title} fallbackIndex={index} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          {HeartBtn}
          {PopularBadge}
        </div>
        <div className="p-3.5 flex flex-col gap-2.5 flex-1">
          {Badges}
          <button type="button" onClick={goDetail} className="text-left font-semibold text-[15px] text-[var(--navy)] hover:text-[var(--yellow-2)] transition leading-tight">{tour.title}</button>
          {AllInclusive}
          {MetaRow}
          <div className="border-t border-gray-100 pt-2.5">{Highlights}</div>
          {FillingRow}
          <div className="mt-auto grid grid-cols-2 gap-2 items-end">
            {PriceBox}
            <div className="flex justify-end">{ViewBtn}</div>
          </div>
          {Footer}
        </div>
        {Popups}
      </motion.div>
    );
  }

  // list view
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.05 }}
      className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-[0_20px_40px_-20px_rgba(11,37,69,0.3)] hover:border-[#f59e0b]/40 transition-all"
      data-testid={`tour-card-${tour.slug}`}
    >
      <div className="grid md:grid-cols-[280px_1fr_230px] gap-0 md:h-[230px]">
        <div className="relative h-52 md:h-full overflow-hidden">
          <SmartImage src={tour.image} alt={tour.title} fallbackIndex={index} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          {HeartBtn}
          {PopularBadge}
        </div>
        <div className="p-4 flex flex-col gap-2 overflow-hidden">
          <div className="flex items-start justify-between gap-2">
            <button type="button" onClick={goDetail} className="text-left font-semibold text-[17px] text-[var(--navy)] hover:text-[var(--yellow-2)] transition line-clamp-2 leading-tight">{tour.title}</button>
            {Badges}
          </div>
          {AllInclusive}
          {MetaRow}
          <div className="border-t border-gray-100 pt-2.5 mt-1">{Highlights}</div>
          {FillingRow}
        </div>
        <div className="p-4 border-l border-gray-100 flex flex-col gap-3 justify-center">
          <div className="text-center">
            <div className="text-[11px] text-gray-500">Starting price per person</div>
            <div className="text-[22px] font-bold text-[var(--navy)]">{formatINR(tour.startingPrice)}</div>
            <div className="text-[11px] text-gray-500">
              EMI from <span className="text-[var(--navy)] font-semibold underline">{tour.emiPerMonth == null ? "Price on request" : formatINR(tour.emiPerMonth)}</span>{tour.emiPerMonth == null ? "" : " /month"}
            </div>
          </div>
          {ViewBtn}
          <div className="flex items-center justify-between text-[12.5px] pt-1">
            <button
              type="button"
              onClick={() => toggleCompare(tour)}
              data-testid={`compare-${tour.slug}`}
              className={`inline-flex items-center gap-1 font-medium transition ${compared ? "text-[var(--yellow-2)]" : "text-[#0b2545]/80 hover:text-[var(--yellow-2)]"}`}
            >
              <ArrowLeftRight className="w-3.5 h-3.5" /> {compared ? "Added" : "Compare"}
            </button>
            <button type="button" onClick={() => setEnquiry(true)} data-testid={`enquire-${tour.slug}`} className="inline-flex items-center gap-1 font-medium text-[#0b2545]/80 hover:text-[var(--yellow-2)]">
              <MessageSquare className="w-3.5 h-3.5" /> Enquire Now
            </button>
          </div>
        </div>
      </div>
      {Popups}
    </motion.div>
  );
};

